import React, { useContext } from 'react';
import { useFormContext } from 'react-hook-form';
import { Icon, Stack, Tooltip } from '@fashionnovateam/nova-ui-toolkit';
import { ItemLeadTimeFormValues } from '../../types';
import { ControlledFormInput } from '../../../../Lib/Utils/FormUtils/ControlledFormInput';
import { FeatureFlagsContext } from '../../../../Lib/Contexts/FeatureFlagsContext';
import { BASELINE_COST_TOOLTIP } from '../../../../Lib/Constants/ui-text';
import { FormSection } from '../components/FormSection';
import { useRequestedItemsContext } from '../../hooks/useRequestedItems';

type CostsSectionProps = {
  disabled?: boolean;
};

function formatCurrency(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '-';
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (Number.isNaN(numValue)) return '-';
  return `$${numValue.toFixed(2)}`;
}

function BaselineCostLabel({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center gap-1 mt-2">
      <Tooltip content={BASELINE_COST_TOOLTIP} position="top" arrow zIndex={60}>
        <Icon
          name="information/information-circle"
          size="16"
          className="text-primary-700 flex-shrink-0 cursor-help"
        />
      </Tooltip>
      <span className="text-xs font-medium text-base-primary">
        {label}: {value}
      </span>
    </div>
  );
}

export function CostsSection({ disabled }: CostsSectionProps) {
  const { control } = useFormContext<ItemLeadTimeFormValues>();
  const { flags } = useContext(FeatureFlagsContext);
  const { selectedItem } = useRequestedItemsContext();

  const showBaselineCost = flags?.baseline_cost?.enabled;

  return (
    <FormSection
      icon="money-dollar-bag"
      title="Costs"
      gradientFrom="from-[#EDE9FE]"
      gradientTo="to-[#F5F3FF]"
      bgColor="bg-purple-50"
    >
      <Stack direction="row" gap="md">
        <div className="w-full">
          <ControlledFormInput
            control={control}
            name="cost_jr"
            label="JR Cost"
            inputType="currency"
            required
            disabled={disabled}
            inputProps={{ className: 'w-full' }}
          />
          {showBaselineCost && (
            <BaselineCostLabel
              label="Baseline JR Cost"
              value={formatCurrency(selectedItem?.baseline_cost_jr)}
            />
          )}
        </div>
        <div className="w-full">
          <ControlledFormInput
            control={control}
            name="cost_plus"
            label="Plus Cost"
            inputType="currency"
            required
            disabled={disabled}
            inputProps={{ className: 'w-full' }}
          />
          {showBaselineCost && (
            <BaselineCostLabel
              label="Baseline Plus Cost"
              value={formatCurrency(selectedItem?.baseline_cost_plus)}
            />
          )}
        </div>
      </Stack>
    </FormSection>
  );
}
